import { useState, useEffect, useCallback, FC } from 'react';
import { useToast } from '../ui/Toast';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { MetricWidget } from '../widgets/MetricWidget';
import { DonutChart } from './DonutChart';
import {
  fetchDashboardAnalytics,
  DashboardAnalyticsResponse,
} from '../../api/analytics';
import {
  Send,
  Clock,
  Users,
  MousePointerClick,
  AlertTriangle,
  AlertCircle,
  ArrowUpRight,
  ExternalLink,
  Store,
  Share2,
  RefreshCw,
  ShoppingBag,
} from 'lucide-react';

interface DashboardViewProps {
  whatsappConnected?: boolean;
  onNavigate: (tab: string, offerId?: string | null) => void;
  token: string;
}

export const DashboardView: FC<DashboardViewProps> = ({
  whatsappConnected = false,
  onNavigate,
  token,
}) => {
  const { error: showError } = useToast();
  const [period, setPeriod] = useState<'1h' | 'today' | '7d' | '30d'>('today');
  const [data, setData] = useState<DashboardAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = useCallback(
    async (silent = false) => {
      if (!token) return;
      if (!silent) {
        setIsLoading(true);
        setLoadError(null);
      }
      try {
        const response = await fetchDashboardAnalytics(token, period);
        setData(response);
        setLoadError(null);
      } catch (err: unknown) {
        if (!silent) {
          const message =
            err instanceof Error
              ? err.message
              : 'Falha ao carregar métricas do dashboard.';
          setLoadError(message);
          showError(message, 'Erro de Carregamento');
        }
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [token, period, showError]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Polling silencioso a cada 30 segundos quando a aba estiver visível
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadData(true);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const kpis = data?.kpis;

  // Filtra e prioriza alertas com base no funil operacional:
  // Se o WhatsApp estiver desconectado, não adianta pedir para retomar a fila.
  const activeAlerts = (() => {
    if (!data?.alerts) return [];
    if (!whatsappConnected) {
      const waAlert = {
        id: 'whatsapp_disconnected',
        type: 'danger',
        title: 'WhatsApp Desconectado',
        message:
          'Conecte um aparelho WhatsApp para permitir envios e ativação da esteira.',
        action_label: 'Conectar Linha',
        action_tab: 'whatsapp',
      };
      // Remove alerta de esteira pausada (já que não adianta retomar sem WhatsApp) e insere o de WhatsApp
      const otherAlerts = data.alerts.filter(
        (a) => a.id !== 'engine_paused' && a.id !== 'whatsapp_disconnected'
      );
      return [waAlert, ...otherAlerts];
    }
    return data.alerts;
  })();

  return (
    <div className="space-y-6">
      {/* Header Executivo com Seletor de Período e Badge do WhatsApp */}
      <Card>
        <Card.Body className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-content-main sm:text-lg">
                  Painel Geral de Afiliados
                </h2>
                {/* Badge Singela do Provedor WhatsApp (sem ocupar card dedicado) */}
                <Badge
                  variant={whatsappConnected ? 'online' : 'offline'}
                  withDot
                  pulse={whatsappConnected}
                >
                  {whatsappConnected ? 'WhatsApp Ativo' : 'WhatsApp Offline'}
                </Badge>
              </div>
              <p className="mt-0.5 text-xs text-content-muted">
                Telemetria de distribuição, capacidade de comunidade e cliques
                em ofertas
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Seletor de Período */}
            <div className="flex items-center rounded-lg border border-surface-border bg-surface-subtle p-0.5 text-micro">
              {[
                { label: '1h', value: '1h' as const },
                { label: 'Hoje', value: 'today' as const },
                { label: '7 Dias', value: '7d' as const },
                { label: '30 Dias', value: '30d' as const },
              ].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setPeriod(tab.value)}
                  className={`cursor-pointer rounded-md px-2.5 py-1 font-medium transition-colors ${
                    period === tab.value
                      ? 'shadow-xs bg-surface-panel text-content-main'
                      : 'text-content-muted hover:text-content-main'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <Button
              variant="secondary"
              size="sm"
              leftIcon={
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`}
                />
              }
              onClick={() => loadData()}
              disabled={isLoading}
            >
              Atualizar
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Alertas Operacionais Acionáveis */}
      {activeAlerts.length > 0 && (
        <div className="space-y-2">
          {activeAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`flex items-center justify-between gap-3 rounded-xl border p-3 text-xs ${
                alert.type === 'danger'
                  ? 'border-red-500/20 bg-red-500/10 text-red-800 dark:text-red-200'
                  : 'border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-200'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <AlertTriangle
                  className={`h-4 w-4 flex-shrink-0 ${alert.type === 'danger' ? 'text-red-500' : 'text-amber-500'}`}
                />
                <div>
                  <span className="font-semibold">{alert.title}:</span>{' '}
                  <span
                    className={
                      alert.type === 'danger'
                        ? 'text-red-900/80 dark:text-red-300/80'
                        : 'text-amber-900/80 dark:text-amber-300/80'
                    }
                  >
                    {alert.message}
                  </span>
                </div>
              </div>
              {alert.action_label && alert.action_tab && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onNavigate(alert.action_tab!)}
                  className="flex-shrink-0"
                >
                  {alert.action_label}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Estado de Falha Crítica da API */}
      {loadError && !data ? (
        <Card>
          <Card.Body className="mx-auto max-w-md space-y-4 px-4 py-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400">
              <AlertCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <h4 className="text-sm font-semibold text-content-main">
                Não foi possível recuperar os dados da API
              </h4>
              <p className="text-xs leading-relaxed text-content-muted">
                Ocorreu uma falha ao obter a telemetria do dashboard. Por favor,
                tente novamente.
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
              onClick={() => loadData()}
            >
              Tentar Novamente
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* Top 4 KPIs Operacionais e Comerciais */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* KPI 1: Disparos Realizados */}
            <MetricWidget
              label="Disparos Realizados"
              value={
                kpis
                  ? `${kpis.dispatches_count} / ${kpis.dispatches_safe_capacity}`
                  : '...'
              }
              icon={Send}
              description={
                kpis && (
                  <span className="flex items-center gap-1.5 font-medium">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        kpis.saturation_percentage >= 100
                          ? 'bg-red-500'
                          : kpis.saturation_percentage >= 80
                            ? 'bg-amber-500'
                            : 'bg-brand-500'
                      }`}
                    />
                    {kpis.saturation_percentage}% do teto seguro diário
                  </span>
                )
              }
            />

            {/* KPI 2: Fila de Disparo */}
            <MetricWidget
              label="Ofertas na Fila"
              value={
                kpis
                  ? `${kpis.queued_count} agendada${kpis.queued_count === 1 ? '' : 's'}`
                  : '...'
              }
              icon={Clock}
              description={
                kpis && (
                  <span className="flex items-center gap-1.5 font-medium">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        kpis.is_engine_paused ? 'bg-amber-500' : 'bg-brand-500'
                      }`}
                    />
                    Esteira{' '}
                    {kpis.is_engine_paused ? 'pausada manualmente' : 'ativa'}
                  </span>
                )
              }
            />

            {/* KPI 3: Comunidade / Grupos */}
            <MetricWidget
              label="Audiência / Grupos"
              value={
                kpis
                  ? `${kpis.total_members.toLocaleString('pt-BR')} membros`
                  : '...'
              }
              icon={Users}
              description={
                kpis && (
                  <span className="flex items-center gap-1.5 font-medium">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        kpis.occupancy_percentage >= 85
                          ? 'bg-amber-500'
                          : 'bg-brand-500'
                      }`}
                    />
                    {kpis.occupancy_percentage}% de capacidade total
                  </span>
                )
              }
            />

            {/* KPI 4: Cliques Totais */}
            <MetricWidget
              label="Cliques Totais em Links"
              value={kpis ? kpis.total_clicks.toLocaleString('pt-BR') : '...'}
              icon={MousePointerClick}
              description={
                <span className="flex items-center gap-1 font-medium text-brand-600 dark:text-brand-400">
                  {period === '1h'
                    ? 'Última 1 hora'
                    : period === 'today'
                      ? 'Hoje'
                      : period === '7d'
                        ? 'Últimos 7 dias'
                        : 'Últimos 30 dias'}
                </span>
              }
            />
          </div>

          {/* Bloco Central: Gráficos de Distribuição de Cliques (Donut) */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Distribuição por Loja / Marketplace */}
            <Card>
              <Card.Header
                title={
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4 text-content-muted" />
                    <span className="text-xs font-semibold">
                      Cliques por Loja / Marketplace
                    </span>
                  </div>
                }
              />
              <Card.Body>
                <DonutChart
                  data={data?.marketplaces_breakdown || []}
                  title="Origem do Produto"
                />
              </Card.Body>
            </Card>

            {/* Distribuição por Canal de Destino */}
            <Card>
              <Card.Header
                title={
                  <div className="flex items-center gap-2">
                    <Share2 className="h-4 w-4 text-content-muted" />
                    <span className="text-xs font-semibold">
                      Cliques por Canal de Destino
                    </span>
                  </div>
                }
              />
              <Card.Body>
                <DonutChart
                  data={data?.channels_breakdown || []}
                  title="Origem do Tráfego"
                />
              </Card.Body>
            </Card>
          </div>

          {/* Bloco Inferior: Resultados de Afiliados Externos e Top Ofertas */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Status de Integrações de Afiliados (Shopee, Mercado Livre, Amazon) */}
            <Card className="lg:col-span-1">
              <Card.Header
                title={
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShoppingBag className="h-4 w-4 text-content-muted" />
                      <span className="text-xs font-semibold">
                        APIs Oficiais de Afiliados
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onNavigate('settings')}
                      className="p-1 text-content-muted hover:text-content-main"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                }
              />
              <Card.Body className="space-y-3">
                {data?.affiliate_integrations.map((item) => (
                  <div
                    key={item.platform}
                    className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-subtle p-3 text-xs"
                  >
                    <div>
                      <div className="font-semibold text-content-main">
                        {item.platform}
                      </div>
                      <div className="text-micro text-content-muted">
                        {item.connected
                          ? `Conectado (${item.configured_account})`
                          : 'Nenhuma chave cadastrada'}
                      </div>
                    </div>

                    {item.connected ? (
                      <Badge variant="online" withDot>
                        Ativa
                      </Badge>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onNavigate('settings')}
                      >
                        Conectar
                      </Button>
                    )}
                  </div>
                ))}

                <div className="rounded-xl border border-surface-border bg-surface-panel p-3 text-center">
                  <p className="text-2xs text-content-muted">
                    As comissões são conciliadas diretamente das APIs da Shopee,
                    Mercado Livre e Amazon assim que as chaves forem
                    configuradas.
                  </p>
                </div>
              </Card.Body>
            </Card>

            {/* Top Ofertas Mais Tracionadas */}
            <Card className="lg:col-span-2">
              <Card.Header
                title={
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">
                      Top Ofertas com Mais Cliques
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      rightIcon={<ArrowUpRight className="h-3.5 w-3.5" />}
                      onClick={() => onNavigate('ofertas')}
                    >
                      Ver Todas as Ofertas
                    </Button>
                  </div>
                }
              />
              <Card.Body>
                {data?.top_offers && data.top_offers.length > 0 ? (
                  <div className="divide-y divide-surface-border overflow-hidden">
                    {data.top_offers.map((offer) => (
                      <div
                        key={offer.id}
                        onClick={() => onNavigate('ofertas', offer.id)}
                        className="hover:bg-surface-subtle/50 -mx-2 flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-2.5 transition-colors"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          {/* Mini Preview da Imagem */}
                          {offer.image_url ? (
                            <img
                              src={offer.image_url}
                              alt={offer.title}
                              className="h-10 w-10 flex-shrink-0 rounded-lg border border-surface-border object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-muted">
                              <ShoppingBag className="h-4 w-4" />
                            </div>
                          )}

                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-semibold text-content-main hover:text-brand-500">
                              {offer.title}
                            </div>
                            <div className="mt-0.5 flex items-center gap-2 text-micro text-content-muted">
                              <span className="rounded-md border border-surface-border bg-surface-subtle px-1.5 py-0.5">
                                {offer.marketplace}
                              </span>
                              {offer.promotional_price && (
                                <span className="font-mono text-content-main">
                                  R${' '}
                                  {offer.promotional_price.toLocaleString(
                                    'pt-BR',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-shrink-0 items-center gap-3">
                          <div className="text-right">
                            <div className="font-mono text-xs font-bold text-content-main">
                              {offer.clicks_count}
                            </div>
                            <div className="text-micro text-content-muted">
                              {offer.clicks_count === 1 ? 'clique' : 'cliques'}
                            </div>
                          </div>
                          <ArrowUpRight className="h-4 w-4 text-content-muted" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-xs text-content-muted">
                    Nenhuma oferta cadastrada com cliques no período.
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};
